/**
 * Checks if a value is empty. A value is considered empty if it is:
 * - null or undefined
 * - an empty string, array, or object
 * - a Map or Set with size 0
 * - a number that is NaN
 * - a boolean false
 * - a function (functions are never considered empty)
 *
 * @param value - The value to check
 * @returns True if the value is empty, false otherwise
 *
 * @example
 * isEmpty(null) // true
 * isEmpty(undefined) // true
 * isEmpty("") // true
 * isEmpty([]) // true
 * isEmpty({}) // true
 * isEmpty(new Map()) // true
 * isEmpty(new Set()) // true
 * isEmpty(NaN) // true
 * isEmpty(false) // true
 * isEmpty(0) // false
 * isEmpty("hello") // false
 * isEmpty([1, 2, 3]) // false
 * isEmpty({a: 1}) // false
 * isEmpty(() => {}) // false
 */
export const isEmpty = <T>(value: T): boolean => {
  if (value == null) return true;

  if (typeof value === "string" || Array.isArray(value)) {
    return value.length === 0;
  }

  if (value instanceof Map || value instanceof Set) {
    return value.size === 0;
  }

  if (typeof value === "boolean") {
    return value === false;
  }

  if (typeof value === "number") {
    return Number.isNaN(value);
  }

  if (typeof value === "function") {
    return false;
  }

  if (typeof value === "object") {
    return Object.keys(value).length === 0;
  }

  return false;
};

/**
 * Performs a deep equality check between two values.
 * Handles primitives, objects, arrays, Maps, Sets, and nested structures.
 *
 * @param a - First value to compare
 * @param b - Second value to compare
 * @returns True if values are deeply equal, false otherwise
 *
 * @example
 * isEqual({a: 1, b: [2, 3]}, {a: 1, b: [2, 3]}) // true
 * isEqual([1, 2, 3], [1, 2, 3]) // true
 * isEqual(new Map([['a', 1]]), new Map([['a', 1]])) // true
 * isEqual(NaN, NaN) // true
 * isEqual(0, -0) // false
 */
export const isEqual = <T>(a: T, b: T): boolean => {
  // Handle same reference
  if (Object.is(a, b)) return true;

  // Handle null/undefined
  if (a == null || b == null) return a === b;

  // Handle different types
  if (typeof a !== typeof b) return false;

  // Handle primitives (including NaN)
  if (typeof a !== "object") {
    // Special case for NaN
    if (Number.isNaN(a) && Number.isNaN(b)) return true;
    return a === b;
  }

  // Handle arrays
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((item, index) => isEqual(item, b[index]));
  }

  // Handle Maps
  if (a instanceof Map && b instanceof Map) {
    if (a.size !== b.size) return false;
    for (const [key, value] of a) {
      if (!b.has(key) || !isEqual(value, b.get(key))) return false;
    }
    return true;
  }

  // Handle Sets
  if (a instanceof Set && b instanceof Set) {
    if (a.size !== b.size) return false;
    for (const value of a) {
      if (!b.has(value)) return false;
    }
    return true;
  }

  // Handle Date objects
  if (a instanceof Date && b instanceof Date) {
    return a.getTime() === b.getTime();
  }

  // Handle RegExp objects
  if (a instanceof RegExp && b instanceof RegExp) {
    return a.source === b.source && a.flags === b.flags;
  }

  // Handle mixed types (array vs non-array, etc.)
  if (Array.isArray(a) || Array.isArray(b)) return false;
  if (a instanceof Map || b instanceof Map) return false;
  if (a instanceof Set || b instanceof Set) return false;
  if (a instanceof Date || b instanceof Date) return false;
  if (a instanceof RegExp || b instanceof RegExp) return false;

  // Handle plain objects
  const keysA = Object.keys(a as Record<string, unknown>);
  const keysB = Object.keys(b as Record<string, unknown>);

  if (keysA.length !== keysB.length) return false;

  for (const key of keysA) {
    if (!keysB.includes(key)) return false;
    if (
      !isEqual(
        (a as Record<string, unknown>)[key],
        (b as Record<string, unknown>)[key],
      )
    )
      return false;
  }

  return true;
};

/**
 * Reduces a collection to a single value by applying a function to each element.
 * Works with objects, arrays, Maps, and Sets.
 *
 * @param collection - The collection to iterate over
 * @param iteratee - The function invoked per iteration
 * @param accumulator - The initial value
 * @returns The accumulated value
 *
 * @example
 * reduce([1, 2, 3], (sum, n) => sum + n, 0) // 6
 * reduce({a: 1, b: 2}, (result, value, key) => result + value, 0) // 3
 * reduce(new Map([['a', 1], ['b', 2]]), (sum, value) => sum + value, 0) // 3
 */
export const reduce = <T, U>(
  collection:
    | T[]
    | Record<string, T>
    | Map<string, T>
    | Set<T>
    | null
    | undefined,
  iteratee: (accumulator: U, value: T, key: string | number) => U,
  accumulator: U,
): U => {
  // Handle null/undefined collections
  if (collection == null) return accumulator;

  if (Array.isArray(collection)) {
    return collection.reduce(
      (acc, value, index) => iteratee(acc, value, index),
      accumulator,
    );
  }

  if (collection instanceof Map) {
    let result = accumulator;
    for (const [key, value] of collection) {
      result = iteratee(result, value, key);
    }
    return result;
  }

  if (collection instanceof Set) {
    let result = accumulator;
    let index = 0;
    for (const value of collection) {
      result = iteratee(result, value, index.toString());
      index++;
    }
    return result;
  }

  // Handle plain objects
  let result = accumulator;
  for (const [key, value] of Object.entries(collection)) {
    result = iteratee(result, value, key);
  }
  return result;
};

/**
 * Capitalizes the first character of a string.
 * This is a simpler version than capitalizeFully - it only capitalizes the first letter
 * without changing the case of the rest of the string.
 *
 * @param str - The string to capitalize
 * @returns The string with the first character capitalized
 *
 * @example
 * capitalize("hello") // "Hello"
 * capitalize("HELLO") // "HELLO"
 * capitalize("") // ""
 */
export const capitalize = <T extends string>(str: T): string => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * A no-operation function that does nothing.
 * Useful as a default value for optional callbacks.
 *
 * @returns undefined
 */
export const noop = (): void => {};

/**
 * Creates a debounced function that delays invoking the provided function
 * until after the specified delay has elapsed since the last time it was invoked.
 *
 * @param func - The function to debounce
 * @param delay - The number of milliseconds to delay
 * @param options - Options object with leading and trailing flags
 * @returns A debounced function with cancel and flush methods
 *
 * @example
 * const debouncedFn = debounce((value) => console.log(value), 300);
 * debouncedFn("hello"); // Will only log after 300ms of inactivity
 * debouncedFn.cancel(); // Cancel pending invocation
 * debouncedFn.flush(); // Immediately invoke if pending
 */
export const debounce = <T extends (...args: unknown[]) => unknown>(
  func: T,
  delay: number,
): T & {cancel: () => void; flush: () => void} => {
  let timeoutId: NodeJS.Timeout | null = null;
  let lastArgs: Parameters<T> | null = null;

  const debounced = ((...args: Parameters<T>) => {
    lastArgs = args;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => {
      func(...lastArgs!);
      timeoutId = null;
      lastArgs = null;
    }, delay);
  }) as T & {cancel: () => void; flush: () => void};

  debounced.cancel = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    lastArgs = null;
  };

  debounced.flush = () => {
    if (timeoutId && lastArgs) {
      clearTimeout(timeoutId);
      func(...lastArgs);
      timeoutId = null;
      lastArgs = null;
    }
  };

  return debounced;
};

/**
 * Capitalizes the first character of the settings string and lowercases the rest.
 *
 * This is useful when normalizing user settings, labels, or converting constants into
 * human-friendly formats (e.g., "tEST" => "Test").
 *
 * @param input - The string to be transformed.
 * @returns A new string with the first letter capitalized and the rest in lowercase.
 *
 * @example
 * capitalizeFully("tEST") // "Test"
 * capitalizeFully("NAME") // "Name"
 * capitalizeFully("")     // ""
 */
export const capitalizeFully = <T extends string>(input: T): string => {
  if (!input) return "";
  return input.charAt(0).toUpperCase() + input.slice(1).toLowerCase();
};

/**
 * Converts a SCREAMING_SNAKE_CASE string into a human-readable sentence format,
 * capitalizing only the first word and transforming underscores to spaces.
 *
 * This is useful for displaying constants or enum values in a friendly format
 * (e.g., for dropdowns, labels, or messages).
 *
 * @param value - The SCREAMING_SNAKE_CASE string to convert.
 * @returns A sentence with the first word capitalized and the rest lowercase.
 *
 * @example
 * screamingSnakeToSentence("ROLE_MODEARTOR") // "Role moderator"
 * screamingSnakeToSentence("API_ERROR_OCCURRED") // "Api error occurred"
 */
export const screamingSnakeToSentence = <T extends string>(
  value: T,
): string => {
  return value
    .toLowerCase()
    .split("_")
    .map((word, i) =>
      i === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word,
    )
    .join(" ");
};

/**
 * Compares a mutated object against its pristine (original) version
 * and returns only the fields that have changed.
 *
 * @template T - The shape of the object being compared.
 * @param {T} mutated - The current (possibly modified) object.
 * @param {Partial<T> | undefined} pristine - The original state of the object, or undefined.
 * @returns {Partial<T>} A new object containing only the fields whose values differ
 * from the pristine version.
 *
 * @example
 * const mutated = { name: "Alice", age: 30, city: "Paris" };
 * const pristine = { name: "Alice", age: 29, city: "Paris" };
 *
 * const result = getModifiedFields(mutated, pristine);
 * // result = { age: 30 }
 */
export const getModifiedFields = <T extends Record<string, unknown>>(
  mutated: T,
  pristine: Partial<T> | undefined,
): Partial<T> => {
  return reduce(
    mutated,
    (result, value, key) => {
      if (!isEqual(value, pristine?.[key])) {
        result[key as keyof T] = value as T[keyof T];
      }
      return result;
    },
    {} as Partial<T>,
  );
};

/**
 * Creates filter options for dropdown/autocomplete components with a consistent "All X" pattern.
 *
 * This utility function generates a standardized array of filter options that includes
 * an "All X" option followed by individual items. The "All X" option uses a kebab-case
 * value (e.g., "all-countries") while maintaining a proper label (e.g., "All Countries").
 *
 * @template T - The type of items in the array
 * @param items - Array of items to create options from
 * @param getValue - Function to extract the value from each item
 * @param getLabel - Function to extract the display label from each item
 * @param allLabel - The label for the "All X" option (e.g., "All Countries")
 * @returns Array of filter options with consistent structure
 *
 * @example
 * const countries = [{ name: "Kenya", code: "ke" }, { name: "Uganda", code: "ug" }];
 * const options = createFilterOptions(
 *   countries,
 *   c => c.code,
 *   c => c.name,
 *   "All Countries"
 * );
 * // Result: [
 * //   { value: "all-countries", label: "All Countries" },
 * //   { value: "ke", label: "Kenya" },
 * //   { value: "ug", label: "Uganda" }
 * // ]
 */
export const createFilterOptions = <T>(
  items: T[],
  getValue: (item: T) => string,
  getLabel: (item: T) => string,
  allLabel: string,
): Array<{value: string; label: string}> => [
  {value: allLabel.toLowerCase().replace(/\s+/g, "-"), label: allLabel},
  ...items.map(item => ({
    value: getValue(item),
    label: getLabel(item),
  })),
];
